import { EN_SOURCE } from './en.js';

/**
 * The message catalog: **keys in code, translations in data.**
 *
 * The same split this codebase already makes twice. A permission is defined in
 * `access/permissions.ts` and granted in `mki_role_permissions`; a page is defined
 * in `access/pages.ts` and granted in `mki_role_pages`. A message key is defined
 * here — because a key is a promise that some component renders it — and its
 * translations live in `mki_translations`, because those are content the client
 * will revise for years without a release.
 *
 * The consequence that matters: an administrator can add a language and type its
 * strings, but cannot invent a key. A key nothing renders is a translation
 * somebody paid for that never appears, and nothing would ever say so.
 *
 * English is the source text, not a translation. It ships in the bundle so the
 * app renders before any network call, and it is the fallback for every key a
 * language has not covered.
 */
export const EN = EN_SOURCE;

export type Messages = Record<string, string>;

/** `{ a: { b: 'x' } }` becomes `{ 'a.b': 'x' }` — how i18next addresses a key. */
export function flatten(tree: unknown, prefix = ''): Messages {
  const out: Messages = {};
  if (tree === null || typeof tree !== 'object') return out;
  for (const [k, v] of Object.entries(tree as Record<string, unknown>)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') out[key] = v;
    else if (v && typeof v === 'object') Object.assign(out, flatten(v, key));
  }
  return out;
}

/** Every key the interface can render, flattened. The vocabulary. */
export const EN_MESSAGES: Messages = flatten(EN);
export const MESSAGE_KEYS: readonly string[] = Object.keys(EN_MESSAGES).sort();
export const MESSAGE_KEY_SET: ReadonlySet<string> = new Set(MESSAGE_KEYS);

/**
 * Text direction is a property of the language, not a preference.
 *
 * Held here rather than asked of the administrator, because getting it wrong
 * produces a page nobody can read and there is exactly one right answer per
 * language. An unlisted language is left-to-right, which is the safe default and
 * true of most of them.
 */
const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'yi', 'dv', 'ku', 'ckb']);

export type Direction = 'ltr' | 'rtl';

/** `ar`, `ar-AE` and `AR` all resolve the same way. */
export function directionFor(code: string): Direction {
  const base = code.toLowerCase().split(/[-_]/)[0] ?? '';
  return RTL_LANGUAGES.has(base) ? 'rtl' : 'ltr';
}

/** BCP-47-ish: `en`, `ar`, `pt-BR`. Deliberately narrow — it names a file and a URL. */
export const LOCALE_CODE = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/;
